import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon
} from '@mui/icons-material';

interface CertificateDialogProps {
  open: boolean;
  onClose: () => void;
  onIssue: (name: string) => void;
  moduleName: string;
  certificateId?: string;
  issuedDate?: Date;
  userName?: string;
}

const CertificateDialog: React.FC<CertificateDialogProps> = ({
  open,
  onClose,
  onIssue,
  moduleName,
  certificateId,
  issuedDate,
  userName = ''
}) => {
  const [name, setName] = useState(userName);
  
  // Update name when userName prop changes
  React.useEffect(() => {
    if (userName && !name) {
      setName(userName);
    }
  }, [userName, name]);

  const handleIssue = () => {
    if (name.trim()) {
      onIssue(name.trim());
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    // Determine certificate title based on module
    const isVanguard = moduleName.toLowerCase().includes('vanguard');
    const dateStr = issuedDate ? new Date(issuedDate).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '-') : new Date().toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '-');
    const certificateTitle = isVanguard 
      ? `E-Unify Vanguard Certification - ${name} - ${dateStr}`
      : 'E-Unify Training Certificate';

    const certificateHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${certificateTitle}</title>
        <link href="https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700&family=Source+Sans+Pro:wght@400;600&display=swap" rel="stylesheet">
        <style>
          @page { size: landscape; margin: 0.5in; }
          body { 
            font-family: 'Source Sans Pro', sans-serif; 
            margin: 0; 
            padding: 20px;
            background: white;
          }
          .certificate {
            border: 8px double #003366;
            padding: 40px;
            max-width: 900px;
            margin: 0 auto;
            background: white;
            position: relative;
          }
          .logo { text-align: center; margin-bottom: 20px; }
          .logo img { height: 60px; }
          .header {
            text-align: center;
            margin-bottom: 30px;
          }
          .title {
            font-family: 'Merriweather', serif;
            font-size: 36px;
            color: #003366;
            margin: 10px 0;
          }
          .subtitle {
            font-size: 24px;
            color: #B31B1B;
            font-weight: 600;
          }
          .recipient {
            text-align: center;
            margin: 30px 0;
            font-size: 18px;
          }
          .name {
            font-size: 32px;
            font-weight: 700;
            color: #003366;
            margin: 15px 0;
            border-bottom: 2px solid #003366;
            display: inline-block;
            padding: 0 40px 5px;
          }
          .description {
            text-align: center;
            margin: 20px 0;
            font-size: 16px;
            line-height: 1.6;
          }
          .signature {
            margin-top: 40px;
            text-align: center;
          }
          .sig-line {
            font-family: 'Brush Script MT', cursive;
            font-size: 32px;
            color: #003366;
            margin-bottom: 5px;
          }
          .sig-title {
            font-size: 14px;
            color: #71767A;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 12px;
            color: #71767A;
          }
          .footer-item {
            display: inline-block;
            margin: 0 20px;
          }
          .footer-label {
            font-weight: 700;
            color: #003366;
          }
        </style>
      </head>
      <body>
        <div class="certificate">
          <div class="logo">
            <img src="/images/eunify-m.png" alt="E-Unify Logo" />
          </div>
          <div class="header">
            <div class="title">${isVanguard ? 'E-Unify Vanguard Certification' : 'Certificate of Completion'}</div>
            <div class="subtitle">${isVanguard ? 'Elite Platform Mastery' : moduleName}</div>
          </div>
          <div class="recipient">
            <div>This certifies that</div>
            <div class="name">${name}</div>
            <div>has successfully completed the training program</div>
          </div>
          <div class="description">
            ${isVanguard 
              ? 'Demonstrating elite mastery of E-Unify platform architecture, data governance,<br/>implementation best practices, and community leadership.' 
              : 'Demonstrating proficiency in the concepts, practices, and applications<br/>covered in this comprehensive training module.'}
          </div>
          <div class="signature">
            <div class="sig-line">Russell B Riehl</div>
            <div class="sig-title">Program Director</div>
          </div>
          <div class="footer">
            <div class="footer-item">
              <span class="footer-label">Certificate ID:</span> ${certificateId || 'PENDING'}
            </div>
            <div class="footer-item">
              <span class="footer-label">Date Issued:</span> ${issuedDate ? new Date(issuedDate).toLocaleDateString() : new Date().toLocaleDateString()}
            </div>
            <div class="footer-item">
              <span class="footer-label">Valid Through:</span> ${new Date(Date.now() + (isVanguard ? 730 : 365)*24*60*60*1000).toLocaleDateString()}
            </div>
          </div>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(certificateHTML);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="certificate-dialog-title"
    >
      <DialogTitle id="certificate-dialog-title">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ color: '#003366' }}>
            Certificate of Completion
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Congratulations! You have successfully completed the <strong>{moduleName}</strong> training module.
          </Typography>
        </Alert>

        {!certificateId ? (
          <>
            <Typography variant="body1" paragraph>
              Enter your name as you would like it to appear on your certificate:
            </Typography>
            <TextField
              fullWidth
              label="Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your full name"
              variant="outlined"
              autoFocus
              sx={{ mb: 2 }}
            />
            <Typography variant="caption" color="text.secondary">
              This name will be displayed on your official certificate of completion.
            </Typography>
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#003366' }}>
              Certificate Issued
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Name:</strong> {name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Certificate ID:</strong> {certificateId}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Issued:</strong> {issuedDate ? new Date(issuedDate).toLocaleDateString() : 'Today'}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} sx={{ color: '#71767A' }}>
          Close
        </Button>
        {!certificateId ? (
          <Button
            variant="contained"
            onClick={handleIssue}
            disabled={!name.trim()}
            sx={{
              bgcolor: '#003366',
              '&:hover': { bgcolor: '#002244' }
            }}
          >
            Issue Certificate
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handlePrint}
            startIcon={<PrintIcon />}
            sx={{
              bgcolor: '#003366',
              '&:hover': { bgcolor: '#002244' }
            }}
          >
            Print Certificate
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CertificateDialog;
